import React, { useMemo } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { CalendarDateInfo } from '../../../types/dentist/calendar';
import DateStatusMarker from '../DateStatusMarker';
import styles from './CalendarDateCell.module.scss';

interface CalendarDateCellProps {
  dateInfo: CalendarDateInfo;
  onClick: (date: Date) => void;
}

/**
 * Calendar date cell component with job status indicators
 * Includes accessibility attributes for Cordova/Web compatibility
 */
const CalendarDateCell: React.FC<CalendarDateCellProps> = ({ dateInfo, onClick }) => {
  const {
    date,
    isToday,
    isPast,
    isCurrentMonth,
    jobs,
    statusClass,
    hasAction,
    shouldShowIndicators,
    hasTarget,
    hasExpiredOffer,
    expiredOfferCount,
    availableShiftCount,
    isPenaltyBlocked,
    activeOfferCount,
    showAutobookSchedule,
  } = dateInfo;

  // Legacy badge helpers expect a data-date attribute formatted as YYYY-M-D (month is zero-based)
  const legacyDataDate = useMemo(() => {
    const year = date.getFullYear();
    const monthZeroBased = date.getMonth();
    const day = date.getDate();
    return `${year}-${monthZeroBased}-${day}`;
  }, [date]);

  // Accessibility attributes for Cordova/Web compatibility (parity with legacy)
  const accessibilityAttrs = useMemo(() => {
    try {
      const m = dayjs(date);
      const iso = m.format('YYYY-MM-DD');
      const spoken = m.format('MMMM D, YYYY');
      return {
        'content-desc': iso,
        'contentDescription': iso,
        'aria-label': spoken
      };
    } catch (e) {
      return {};
    }
  }, [date]);

  const handleClick = () => {
    // Allow clicking past dates if there are jobs (e.g., completed/booked in past)
    // Past dates with no jobs will be handled upstream (shows cannot create shift in past)
    onClick(date);
  };

  return (
    <div
      className={clsx(
        styles.dateCell,
        // Apply hygienist/dentist status class (mapped to CSS Module or fallback raw class)
        statusClass && (styles[statusClass] || statusClass),
        availableShiftCount && (isPenaltyBlocked
          ? styles['badge-white-jobs']
          : styles['badge-green-jobs']),
        activeOfferCount && styles['badge-yellow-offers'],
        showAutobookSchedule && styles['calendar-autobook-available'],
        {
          [styles.today]: isToday,
          [styles.otherMonth]: !isCurrentMonth,
          [styles.past]: isPast,
          [styles.hasJobs]: jobs.length > 0,
          // Clickable if not past, or if there are jobs on a past date (e.g., completed)
          [styles.clickable]: !isPast || jobs.length > 0
        }
      )}
      onClick={handleClick}
      data-date={legacyDataDate}
      {...(availableShiftCount ? { 'data-badge': String(availableShiftCount) } : {})}
      {...(activeOfferCount ? { 'data-offer-badge': String(activeOfferCount) } : {})}
      {...accessibilityAttrs}
    >
      <span className={styles.dateNumber}>
        {date.getDate()}
      </span>
      
      {shouldShowIndicators && (
        <DateStatusMarker
          jobs={jobs}
          hasAction={hasAction}
          isPast={isPast}
          hasTarget={hasTarget}
          hasExpiredOffer={hasExpiredOffer}
          expiredOfferCount={expiredOfferCount}
        />
      )}
    </div>
  );
};

export default CalendarDateCell; 